import { z } from 'zod';
import { bloodGroupSchema, rhFactorSchema, anticoagulantSchema } from './blood';
import { listQuerySchema } from './organization';
export const unitStatusSchema = z.enum([
    'collected', 'screening', 'released', 'available', 'reserved',
    'issued', 'transfused', 'processed', 'quarantined', 'expired', 'discarded',
]);
export const screeningStatusSchema = z.enum(['pending', 'non_reactive', 'reactive']);
export const screeningMarkerSchema = z.enum(['not_tested', 'non_reactive', 'reactive']);
export const donorSexSchema = z.enum(['male', 'female']);
export const deferralTypeSchema = z.enum(['temporary', 'permanent']);
/**
 * Legal status transitions. The server enforces them and the client uses them to decide
 * which buttons appear — one source of truth.
 *
 * A status with no destinations is terminal: `issued` has left stock, `processed` was
 * consumed by a split, and `transfused` and `discarded` are done.
 */
export const UNIT_TRANSITIONS = {
    collected: ['screening', 'quarantined', 'discarded'],
    screening: ['released', 'quarantined', 'discarded'],
    released: ['available', 'processed', 'quarantined', 'discarded'],
    available: ['reserved', 'processed', 'expired', 'quarantined', 'discarded'],
    // Returning to available covers an expired reservation or a rejected order.
    reserved: ['issued', 'available', 'quarantined', 'discarded'],
    issued: ['transfused'],
    // A quarantine can be lifted if screening is later corrected (e.g. a lab error). The
    // database CHECK gate still refuses unless screening is non-reactive.
    quarantined: ['released', 'discarded'],
    expired: ['discarded'],
    transfused: [],
    processed: [],
    discarded: [],
};
export function canTransition(from, to) {
    return UNIT_TRANSITIONS[from].includes(to);
}
export const UNIT_STATUS_LABELS = {
    collected: 'Diambil',
    screening: 'Skrining',
    released: 'Lolos skrining',
    available: 'Tersedia',
    reserved: 'Dialokasikan',
    issued: 'Dikirim',
    transfused: 'Ditransfusikan',
    processed: 'Dipecah',
    quarantined: 'Dikarantina',
    expired: 'Kadaluarsa',
    discarded: 'Dimusnahkan',
};
export const SCREENING_STATUS_LABELS = {
    pending: 'Belum lengkap',
    non_reactive: 'Non-reaktif',
    reactive: 'Reaktif',
};
/** Statuses in which a unit still counts as distributable stock. */
export const DISTRIBUTABLE_STATUSES = ['released', 'available', 'reserved'];
// ───────────────────────────── input ─────────────────────────────
/** Base object kept separate so the microsite variant can drop `phone`. */
const donorBaseSchema = z.object({
    /** Sent once, then hashed. Never stored as plaintext. */
    nik: z.string().trim().min(16, 'NIK harus 16 digit').max(24),
    name: z.string().trim().min(2, 'Nama minimal 2 karakter').max(200),
    dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Format tanggal lahir YYYY-MM-DD'),
    sex: donorSexSchema,
    abo: bloodGroupSchema.optional().nullable(),
    rh: rhFactorSchema.optional().nullable(),
    phone: z.string().trim().max(32).optional().nullable(),
    address: z.string().trim().max(500).optional().nullable(),
    city: z.string().trim().max(200).optional().nullable(),
    /**
     * Staff acknowledge a mismatch between the NIK and the date of birth / sex.
     * Without it, a mismatch blocks registration.
     */
    acknowledgeNikMismatch: z.boolean().optional(),
});
const bloodTypePaired = (v, ctx) => {
    if ((v.abo && !v.rh) || (!v.abo && v.rh)) {
        ctx.addIssue({
            code: 'custom', path: ['rh'],
            message: 'Golongan darah dan rhesus harus diisi bersamaan.',
        });
    }
};
export const donorInputSchema = donorBaseSchema.superRefine(bloodTypePaired);
/**
 * Self-service registration through the microsite. `phone` is DELIBERATELY dropped: the
 * number always comes from the AiYO SSO identity, never from the request body. That is
 * what keeps phone-based identity resolution trustworthy on later visits — a core rule
 * from the microsite integration document.
 */
export const micrositeDonorRegisterSchema = donorBaseSchema
    .omit({ phone: true })
    .superRefine(bloodTypePaired);
export const donationInputSchema = z.object({
    donorId: z.string().uuid(),
    branchId: z.string().uuid(),
    collectedAt: z.string().datetime({ offset: true }),
    volumeMl: z.coerce.number().int().min(1).max(1000),
    anticoagulant: anticoagulantSchema,
    /** Group from laboratory typing, not what the donor reported. */
    abo: bloodGroupSchema,
    rh: rhFactorSchema,
    /** Initial component. WB for whole blood donations, TC-AP for apheresis. */
    initialComponentCode: z.string().trim().min(1).max(16).default('WB'),
    note: z.string().trim().max(500).optional().nullable(),
});
export const screeningInputSchema = z.object({
    hiv: screeningMarkerSchema,
    hbsag: screeningMarkerSchema,
    hcv: screeningMarkerSchema,
    syphilis: screeningMarkerSchema,
    /** Mandatory only in endemic regions. `not_tested` does not block a non-reactive verdict. */
    malaria: screeningMarkerSchema.default('not_tested'),
    testedAt: z.string().datetime({ offset: true }).optional(),
    method: z.string().trim().max(200).optional().nullable(),
    note: z.string().trim().max(500).optional().nullable(),
});
export const splitInputSchema = z.object({
    components: z.array(z.object({
        componentCode: z.string().trim().min(1).max(16),
        volumeMl: z.coerce.number().int().min(1).max(1000),
        count: z.coerce.number().int().min(1).max(12).default(1),
    })).min(1, 'Minimal satu komponen turunan'),
    note: z.string().trim().max(500).optional().nullable(),
});
export const transitionInputSchema = z.object({
    to: unitStatusSchema,
    note: z.string().trim().max(500).optional().nullable(),
    discardReason: z.string().trim().max(500).optional().nullable(),
    temperatureC: z.coerce.number().min(-80).max(50).optional().nullable(),
});
export const transferInputSchema = z.object({
    toBranchId: z.string().uuid(),
    temperatureC: z.coerce.number().min(-80).max(50).optional().nullable(),
    note: z.string().trim().max(500).optional().nullable(),
});
export const stockQuerySchema = z.object({
    branchId: z.string().uuid().optional(),
    componentTypeId: z.string().uuid().optional(),
    abo: bloodGroupSchema.optional(),
    rh: rhFactorSchema.optional(),
});
/**
 * The donation list query. `donorId` is what lets a drive's registration list offer the
 * donations belonging to that one donor — the alternative was making an operator paste a
 * UUID to record that somebody donated.
 */
export const donationListQuerySchema = listQuerySchema.extend({
    donorId: z.string().uuid().optional(),
});
